import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Lightbox } from 'ngx-lightbox';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { SubProject } from 'src/app/model/project/sub-project';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import { PersonNamePipe } from 'src/app/shared/pipes/person-name.pipe';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ClientCornerMapHelper } from '../../helpers/client-corner-map-helper';
import { ClientCornerProjectInfoHelper } from './helpers/client-corner-project-info-helper';

@Component({
  selector: 'client-corner-project-info',
  templateUrl: './client-corner-project-info.component.html',
  styleUrls: ['./client-corner-project-info.component.scss'],
  providers: [PersonNamePipe]
})
export class ClientCornerProjectInfoComponent extends SubscriptionBasedComponent
  implements OnInit {
  private static readonly HIDDEN_CLASS = 'hidden';
  private static readonly NO_PICTURE_URL = '../../../../assets/no-picture.jpg';

  @Input()
  index: number;
  @Input()
  project: Project;
  @Input()
  toggleDisplay: Subject<number>;
  @Input()
  alwaysDisplay: boolean = false;
  @Output()
  backPressed = new EventEmitter<any>();

  baseProject: BaseProject;
  latestSubProject: SubProject;
  latestSubProjectWithAssets: SubProject;
  latestProjectAssetUrl = ClientCornerProjectInfoComponent.NO_PICTURE_URL;

  displayClass: string = ClientCornerProjectInfoComponent.HIDDEN_CLASS;
  displayProjectContactNames: string;
  productTypeDisplays: string[];

  canRequestAdjustment: boolean = false;
  adjustmentButtonLabel: string;
  soldRentedButtonLabel: string;
  repoButtonLabel: string;

  originalPlacementDate: Date;
  tentativeExecutionDate: Date;

  mailContent: string;

  constructor(
    private clientCompanySelectService: ClientCompanySelectService,
    private router: Router,
    private personNamePipe: PersonNamePipe,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.alwaysDisplay) {
      this.displayClass = '';
    } else if (this.toggleDisplay != null) {
      this.addSubscription(this.toggleDisplay, i => {
        this.displayClass =
          i === this.index ? '' : ClientCornerProjectInfoComponent.HIDDEN_CLASS;
      });
    }
    this.baseProject = ProjectHelper.getBaseProject(this.project);
    this.latestSubProject = ClientCornerMapHelper.getMostRecentSubProject(
      this.project
    );
    this.latestSubProjectWithAssets = ClientCornerMapHelper.getMostRecentSubProjectWithAssets(
      this.project
    );
    this.calculateLatestProjectAssetUrl();
    this.preprocessProject();
    this.preprocessProducts();
    this.preprocessLatestSubProject();
    this.mailContent = this.mail();
    const canRequestAdjustmentAndSoldRented = ClientCornerProjectInfoHelper.calculateCanRequestAdjustmentAndSoldRented(
      this.project
    );
    this.canRequestAdjustment = canRequestAdjustmentAndSoldRented[0];
    const actionButtonLabels = ClientCornerProjectInfoHelper.calculateActionButtonLabels(
      this.project
    );
    this.adjustmentButtonLabel = actionButtonLabels[0];
    this.soldRentedButtonLabel = actionButtonLabels[1];
    this.repoButtonLabel = actionButtonLabels[2];
  }

  clickBack(): void {
    this.displayClass = ClientCornerProjectInfoComponent.HIDDEN_CLASS;
    this.backPressed.next();
  }

  openLightbox(): void {
    if (this.latestSubProjectWithAssets == null) {
      return;
    }
    const album = ClientCornerProjectInfoHelper.getLightboxAlbum(
      this.project,
      this.latestSubProjectWithAssets
    );
    if (CollectionHelper.isPopulated(album)) {
      this._lightbox.open(album);
    }
  }

  private calculateLatestProjectAssetUrl(): void {
    const assetUrl =
      this.latestSubProjectWithAssets == null
        ? null
        : ClientCornerMapHelper.getLatestProjectAssetUrl(
            this.project,
            this.latestSubProjectWithAssets
          );
    this.latestProjectAssetUrl =
      assetUrl == null ? this.latestProjectAssetUrl : assetUrl;
  }

  private preprocessProject(): void {
    if (!CollectionHelper.isPopulated(this.project.projectContacts)) {
      return;
    }
    const lookupCompany = this.clientCompanySelectService.getSelectedCompany();
    let toShowProjectContacts = this.project.projectContacts.filter(
      pc => pc.company.id === lookupCompany.id
    );
    toShowProjectContacts =
      toShowProjectContacts.length === 0
        ? this.project.projectContacts
        : toShowProjectContacts;
    this.displayProjectContactNames = StringHelper.join(
      toShowProjectContacts.map(pc =>
        this.personNamePipe.transform(pc.contact.firstName, pc.contact.lastName)
      ),
      ', '
    );
  }

  private preprocessProducts(): void {
    if (!CollectionHelper.isPopulated(this.project.products)) {
      return;
    }

    this.productTypeDisplays = ClientCornerProjectInfoHelper.getProductTypeDisplays(
      this.project.products
    );

    const dateDisplay = ClientCornerProjectInfoHelper.getDateDisplay(
      this.latestSubProject,
      this.project
    );
    for (const product of this.project.products) {
      product.clientCornerTitle = ClientCornerProjectInfoHelper.getProductTitle(
        product
      );
      product.clientCornerDisplay = StringHelper.join(
        [product.displayType, product.clientCornerTitle, dateDisplay],
        ' - '
      );
      product.clientCornerDisplay = StringHelper.join(
        [product.displayType, product.clientCornerTitle, dateDisplay],
        ' - '
      );
    }
  }

  private preprocessLatestSubProject(): void {
    if (this.latestSubProject != null) {
      this.latestSubProject.clientDisplayStatus = ProjectHelper.getClientDisplayStatus(
        this.latestSubProject
      );
      this.latestSubProject.clientCornerPlacerNames = ProjectHelper.getPlacerNames(
        this.latestSubProject,
        this.project
      );
    }

    if (this.baseProject != null) {
      this.baseProject.clientCornerPlacerNames = ProjectHelper.getPlacerNames(
        this.baseProject,
        this.project
      );

      if (ProjectHelper.isIPBoardProject(this.project)) {
        this.originalPlacementDate = this.latestSubProject.currentExecutionDate;
        this.tentativeExecutionDate =
          this.latestSubProject.tourItem != null &&
          this.latestSubProject.tourItem.tour != null
            ? this.latestSubProject.tourItem.tour.date
            : null;
      } else {
        this.originalPlacementDate =
          this.baseProject != null
            ? this.baseProject.currentExecutionDate
            : null;
        this.tentativeExecutionDate = this.latestSubProject.currentConfirmedPlacementDate;
      }
    }
  }

  openProject() {
    this.router.navigateByUrl(
      '/klant-opdracht/' + this.project.id + '/' + this.latestSubProject.id
    );
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  requestAdjustment() {
    if (!this.canRequestAdjustment) {
      return;
    }
    this.router.navigateByUrl(`/klant-opdracht-aanpassing/${this.project.id}`);
  }

  requestSoldRented() {
    this.router.navigateByUrl(
      '/klant-opdracht-verkocht-verhuurd/' + this.project.id
    );
  }

  requestRepo() {
    if (!this.canRequestAdjustment) {
      return;
    }
    this.router.navigateByUrl(`/klant-opdracht-weghalen/${this.project.id}`);
  }

  private mail(): string {
    return (
      'mailto:immo@inpress.be' +
      '?Subject=' +
      this.getMailSubjectLine(this.project) +
      '&body=Beste,'
    );
  }

  private getMailSubjectLine(project: Project): string {
    let toReturn =
      (ProjectHelper.isIPBoardProject(project) ? 'Job #' : 'Project #') +
      project.id;
    if (
      this.baseProject != null &&
      this.baseProject.projectPlacement != null &&
      this.baseProject.projectPlacement.address != null
    ) {
      let addressPart = StringHelper.join(
        [
          this.baseProject.projectPlacement.address.line1,
          StringHelper.join(
            [
              this.baseProject.projectPlacement.address.postalCode,
              this.baseProject.projectPlacement.address.city
            ],
            ' '
          )
        ],
        ', '
      );
      if (!StringHelper.isEmpty(addressPart)) {
        toReturn += ' / ' + addressPart;
      }
    }
    return toReturn;
  }
}
